/**
 * KAN-718: поля SMTP (отправка) и IMAP (приём ответов) своего ящика.
 * Пустой пароль при сохранении оставляет прежний, поэтому при сохранённом
 * пароле поле показывает подсказку вместо требования ввести.
 */
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { PasswordInput } from '@/components/ui/password-input';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { SMTP_SECURITY_OPTIONS, type MailboxFormState } from '@/lib/emailMailboxForm';
import type { SmtpSecurity } from '@/types/emailMailbox';

interface Props {
  form: MailboxFormState;
  onChange: (patch: Partial<MailboxFormState>) => void;
  hasSmtpPassword: boolean;
  hasImapPassword: boolean;
  disabled?: boolean;
}

const SAVED_PASSWORD = 'Сохранён. Оставьте пустым, чтобы не менять';

interface FieldProps {
  id: string;
  label: string;
  children: React.ReactNode;
}

function Field({ id, label, children }: FieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      {children}
    </div>
  );
}

export function MailboxServerFields({
  form,
  onChange,
  hasSmtpPassword,
  hasImapPassword,
  disabled,
}: Props): JSX.Element {
  return (
    <div className="space-y-5">
      <fieldset className="space-y-3" disabled={disabled}>
        <legend className="text-sm font-medium text-gray-900">Отправка (SMTP)</legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr_1fr]">
          <Field id="mail-smtp-host" label="Сервер">
            <Input
              id="mail-smtp-host"
              value={form.smtp_host}
              placeholder="smtp.example.ru"
              onChange={(e) => onChange({ smtp_host: e.target.value })}
            />
          </Field>
          <Field id="mail-smtp-port" label="Порт">
            <Input
              id="mail-smtp-port"
              inputMode="numeric"
              value={form.smtp_port}
              onChange={(e) => onChange({ smtp_port: e.target.value })}
            />
          </Field>
          <Field id="mail-smtp-security" label="Шифрование">
            <NativeSelect
              id="mail-smtp-security"
              value={form.smtp_security}
              options={SMTP_SECURITY_OPTIONS}
              onChange={(e) => onChange({ smtp_security: e.target.value as SmtpSecurity })}
            />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field id="mail-smtp-username" label="Логин">
            <Input
              id="mail-smtp-username"
              autoComplete="off"
              value={form.smtp_username}
              onChange={(e) => onChange({ smtp_username: e.target.value })}
            />
          </Field>
          <Field id="mail-smtp-password" label="Пароль">
            <PasswordInput
              id="mail-smtp-password"
              autoComplete="new-password"
              value={form.smtp_password}
              placeholder={hasSmtpPassword ? SAVED_PASSWORD : ''}
              onChange={(e) => onChange({ smtp_password: e.target.value })}
            />
          </Field>
        </div>
      </fieldset>

      <fieldset className="space-y-3" disabled={disabled}>
        <div className="flex items-center gap-3">
          <Switch
            id="mail-imap-enabled"
            aria-label="Принимать ответы по IMAP"
            checked={form.imap_enabled}
            onCheckedChange={(checked) => onChange({ imap_enabled: checked })}
            disabled={disabled}
          />
          <Label htmlFor="mail-imap-enabled" className="text-sm font-medium text-gray-900">
            Принимать ответы клиентов (IMAP)
          </Label>
        </div>
        {form.imap_enabled && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr_1fr] sm:items-end">
              <Field id="mail-imap-host" label="Сервер">
                <Input
                  id="mail-imap-host"
                  value={form.imap_host}
                  placeholder="imap.example.ru"
                  onChange={(e) => onChange({ imap_host: e.target.value })}
                />
              </Field>
              <Field id="mail-imap-port" label="Порт">
                <Input
                  id="mail-imap-port"
                  inputMode="numeric"
                  value={form.imap_port}
                  onChange={(e) => onChange({ imap_port: e.target.value })}
                />
              </Field>
              <div className="flex h-10 items-center gap-2">
                <Checkbox
                  id="mail-imap-ssl"
                  checked={form.imap_ssl}
                  onCheckedChange={(checked) => onChange({ imap_ssl: checked === true })}
                />
                <Label htmlFor="mail-imap-ssl" className="text-xs">
                  SSL
                </Label>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field id="mail-imap-username" label="Логин">
                <Input
                  id="mail-imap-username"
                  autoComplete="off"
                  value={form.imap_username}
                  onChange={(e) => onChange({ imap_username: e.target.value })}
                />
              </Field>
              <Field id="mail-imap-password" label="Пароль">
                <PasswordInput
                  id="mail-imap-password"
                  autoComplete="new-password"
                  value={form.imap_password}
                  placeholder={hasImapPassword ? SAVED_PASSWORD : ''}
                  onChange={(e) => onChange({ imap_password: e.target.value })}
                />
              </Field>
            </div>
          </>
        )}
      </fieldset>
    </div>
  );
}
